import { useMemo } from "react";
import { PieChart, TrendingUp } from "lucide-react";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Cell, LabelList,
} from "recharts";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { fmtBRL, fmtInt, fmtPct } from "@/lib/format";
import { planPrice, planLucroLiquido } from "@/lib/plans";

const BAR_COLORS = [
  "hsl(var(--chart-1))",
  "hsl(var(--chart-2))",
  "hsl(var(--chart-3))",
  "hsl(var(--chart-4))",
  "hsl(var(--chart-5))",
  "#10b981",
  "#f59e0b",
  "#8b5cf6",
];

export default function MargemPlanoCard({ clientes = [], plans = [] }) {
  const rows = useMemo(() => {
    if (!plans.length) return [];
    // conta vendas por plano no período
    const counter = new Map();
    for (const c of clientes) {
      const name = c.plano_escolhido || "—";
      counter.set(name, (counter.get(name) || 0) + 1);
    }
    // monta linha por plano CADASTRADO (mesmo com 0 vendas mostra a margem unitária)
    const arr = plans.map((p) => {
      const vendas = counter.get(p.name) || 0;
      const price = Number(p.price || 0);
      const lucroUnit = Number(p.lucro_liquido || 0);
      const lucroTotal = lucroUnit * vendas;
      const receitaTotal = price * vendas;
      const margemPct = price > 0 ? (lucroUnit / price) * 100 : 0;
      return {
        plano: p.name,
        vendas,
        price,
        lucroUnit,
        lucroTotal,
        receitaTotal,
        margemPct,
      };
    });
    return arr;
  }, [clientes, plans]);

  const totalLucro = rows.reduce((s, r) => s + r.lucroTotal, 0);
  const bestByLucroTotal = [...rows].sort((a, b) => b.lucroTotal - a.lucroTotal)[0];
  const bestByMargem = [...rows].sort((a, b) => b.margemPct - a.margemPct)[0];

  // dados p/ o gráfico (só planos com preço definido)
  const chartData = rows.filter((r) => r.price > 0);

  return (
    <div className="rounded-xl border border-border bg-card p-5" data-testid="margem-plano-card">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <PieChart className="w-4 h-4 text-primary" />
          <h3 className="font-heading font-semibold text-lg">Margem por plano</h3>
        </div>
        <Badge variant="outline">Lucro líquido no período</Badge>
      </div>

      {/* Resumo */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-5">
        <div className="rounded-lg border border-border p-3">
          <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Lucro total</div>
          <div className="font-heading text-2xl font-extrabold text-emerald-500 tabular-nums mt-0.5">
            {fmtBRL(totalLucro)}
          </div>
          <div className="text-xs text-muted-foreground mt-0.5">
            Soma dos lucros unitários × vendas
          </div>
        </div>
        <div className="rounded-lg border border-border p-3">
          <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Maior lucro no período</div>
          <div className="font-heading text-lg font-bold truncate mt-0.5">
            {bestByLucroTotal?.plano || "—"}
          </div>
          <div className="text-xs text-emerald-500 mt-0.5 tabular-nums">
            {bestByLucroTotal ? fmtBRL(bestByLucroTotal.lucroTotal) : "—"}
            {bestByLucroTotal ? ` · ${fmtInt(bestByLucroTotal.vendas)} venda(s)` : ""}
          </div>
        </div>
        <div className="rounded-lg border border-border p-3">
          <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Melhor margem unitária</div>
          <div className="font-heading text-lg font-bold truncate mt-0.5">
            {bestByMargem?.plano || "—"}
          </div>
          <div className="text-xs text-primary mt-0.5 tabular-nums">
            {bestByMargem ? fmtPct(bestByMargem.margemPct) : "—"}
            {bestByMargem ? ` · lucro ${fmtBRL(bestByMargem.lucroUnit)}` : ""}
          </div>
        </div>
      </div>

      {/* Gráfico */}
      <div className="h-72" data-testid="margem-chart">
        {chartData.length === 0 ? (
          <div className="h-full grid place-items-center text-sm text-muted-foreground">
            Cadastre planos com valor de venda para visualizar o gráfico.
          </div>
        ) : (
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={chartData} margin={{ top: 24, right: 12, left: -8, bottom: 0 }}>
              <CartesianGrid stroke="hsl(var(--border))" vertical={false} strokeDasharray="3 3" />
              <XAxis dataKey="plano" stroke="hsl(var(--muted-foreground))" fontSize={12} />
              <YAxis
                stroke="hsl(var(--muted-foreground))"
                fontSize={12}
                tickFormatter={(v) => fmtBRL(v).replace(/\s/g, "")}
              />
              <Tooltip
                contentStyle={{
                  background: "hsl(var(--card))",
                  border: "1px solid hsl(var(--border))",
                  borderRadius: 8,
                  fontSize: 12,
                }}
                formatter={(val, name, ctx) => {
                  const r = ctx?.payload;
                  return [
                    fmtBRL(val),
                    `${name} · ${r?.vendas ?? 0} venda(s) · margem ${fmtPct(r?.margemPct || 0)}`,
                  ];
                }}
              />
              <Bar dataKey="lucroTotal" name="Lucro no período" radius={[8, 8, 0, 0]}>
                {chartData.map((row, idx) => (
                  <Cell key={row.plano || row.name || `plano-${idx}`} fill={BAR_COLORS[idx % BAR_COLORS.length]} />
                ))}
                <LabelList
                  dataKey="lucroTotal"
                  position="top"
                  formatter={(v) => (v > 0 ? fmtBRL(v) : "")}
                  style={{ fill: "hsl(var(--foreground))", fontSize: 11, fontWeight: 600 }}
                />
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        )}
      </div>

      {/* Ranking detalhado */}
      <div className="mt-6 space-y-2.5" data-testid="margem-ranking">
        <div className="flex items-center gap-2 mb-2">
          <TrendingUp className="w-4 h-4 text-primary" />
          <h4 className="font-heading font-semibold">Detalhes por plano</h4>
        </div>
        <div className="grid grid-cols-[1fr_80px_120px_120px_120px_1fr] gap-3 text-[10px] uppercase tracking-wider text-muted-foreground px-1">
          <div>Plano</div>
          <div className="text-right">Vendas</div>
          <div className="text-right">Valor venda</div>
          <div className="text-right">Lucro unit.</div>
          <div className="text-right">Lucro total</div>
          <div>Margem</div>
        </div>
        {rows.map((r, idx) => {
          const tone =
            r.margemPct >= 45 ? "text-emerald-500" : r.margemPct >= 30 ? "text-amber-500" : "text-red-500";
          return (
            <div
              key={r.plano}
              className="grid grid-cols-[1fr_80px_120px_120px_120px_1fr] gap-3 items-center text-sm rounded-md border border-border px-3 py-2"
              data-testid={`margem-row-${r.plano}`}
            >
              <div className="flex items-center gap-2 min-w-0">
                <span
                  className="w-2.5 h-2.5 rounded-full shrink-0"
                  style={{ background: BAR_COLORS[idx % BAR_COLORS.length] }}
                />
                <span className="truncate font-medium">{r.plano}</span>
              </div>
              <div className="text-right tabular-nums">{fmtInt(r.vendas)}</div>
              <div className="text-right tabular-nums font-mono text-muted-foreground">{fmtBRL(r.price)}</div>
              <div className="text-right tabular-nums font-mono text-emerald-500">{fmtBRL(r.lucroUnit)}</div>
              <div className="text-right tabular-nums font-mono font-semibold">{fmtBRL(r.lucroTotal)}</div>
              <div className="flex items-center gap-2 min-w-0">
                <Progress value={Math.min(r.margemPct, 100)} className="h-1.5 flex-1" />
                <span className={`text-xs font-semibold tabular-nums w-14 text-right ${tone}`}>
                  {fmtPct(r.margemPct)}
                </span>
              </div>
            </div>
          );
        })}
        {rows.length === 0 && (
          <div className="text-sm text-muted-foreground text-center py-4">
            Nenhum plano cadastrado.
          </div>
        )}
      </div>
    </div>
  );
}
